import {
	AddIcon,
	Button,
	CopyIcon,
	DeleteIcon,
	Modal,
	ModuleHeader,
	PieChartIcon,
	SearchIcon,
	SpinnerWithBackground,
	Text,
} from '@viapeople/components'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type Column, GenericTable } from '../../components/GenericTable'
import { useModulePermission } from '../../hooks/useModulePermission'
import { sessionStore } from '../../sharedStores/sessionStore'
import { useToast } from '../../utils/ToastContext'
import type { LoopDashboardStoreInstance, LoopListItem, SearchParams } from './stores/loopDashboardStore'

interface LoopsPageProps {
	store: LoopDashboardStoreInstance
}

export const LoopsPage = ({ store }: LoopsPageProps) => {
	const {
		loops,
		selectedLoop,
		organization,
		actions,
		isLoading,
		error,
		searchQuery,
		initialize,
		search,
		duplicateSelectedLoop,
		deleteSelectedLoop,
		setSelectedLoop,
		setSearchQuery,
		setError,
	} = store()
	const { user } = sessionStore()
	const organizationId = user?.organizationId || ''
	const navigate = useNavigate()
	const toast = useToast()
	const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	// Check Loops module permission
	const { isLoading: isCheckingPermission, isAuthorized, role } = useModulePermission('loops')
	const isViewerAdminOnly = role === 'viewerAdmin'

	// Redirect unauthorized users
	useEffect(() => {
		if (!isCheckingPermission && !isAuthorized) {
			toast.showToast('You do not have permission to access the Loops module', 'error')
			navigate('/')
		}
	}, [isCheckingPermission, isAuthorized, navigate, toast])

	useEffect(() => {
		if (organizationId) {
			initialize(organizationId)
		}
	}, [organizationId, initialize])

	useEffect(() => {
		if (error) {
			toast.showToast(error, 'error')
			setError()
		}
	}, [error, toast, setError])

	const handleOpenDuplicateModal = (loop: LoopListItem) => {
		setSelectedLoop(loop)
		setIsDuplicateModalOpen(true)
	}

	const handleOpenDeleteModal = (loop: LoopListItem) => {
		setSelectedLoop(loop)
		setIsDeleteModalOpen(true)
	}

	const handleDuplicate = async () => {
		try {
			const newLoopId = await duplicateSelectedLoop()
			toast.showToast('Loop duplicated successfully')
			navigate(`/loops/${newLoopId}/general`)
		} catch {
			// Error handling is done in the store
		} finally {
			setIsDuplicateModalOpen(false)
		}
	}

	const handleDelete = async () => {
		try {
			await deleteSelectedLoop()
			toast.showToast('Loop deleted successfully')
		} catch {
			// Error handling is done in the store
		} finally {
			setIsDeleteModalOpen(false)
		}
	}

	const handleRowClick = (loop: LoopListItem) => {
		navigate(`/loops/${loop.id}/general`)
	}

	const handleViewResults = (loopId: string) => {
		navigate(`/loops/${loopId}/results`)
	}

	// Handle external search changes with debouncing
	const handleSearchChange = useCallback(
		(value: string) => {
			setSearchQuery(value)

			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}

			searchTimeoutRef.current = setTimeout(() => {
				setSearchQuery(value)

				if (organizationId) {
					search()
				}
			}, 300)
		},
		[organizationId, setSearchQuery, search],
	)

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [])

	// Query options generator for GenericTable
	const queryOptionsGenerator = useCallback((_options: SearchParams & { organizationId: string }) => {
		// Return empty query options since we're not using React Query
		return { enabled: false }
	}, [])

	// Create conditional empty state based on search context
	const getEmptyState = useMemo(() => {
		// If user is searching and no results found
		if (searchQuery.trim().length > 0) {
			return {
				icon: SearchIcon,
				title: 'No loops match your search',
				description: 'Try adjusting your search terms or clear the search to see all loops',
			}
		}

		// Default empty state when no loops exist and no search active
		return {
			icon: SearchIcon,
			title: 'No loops created yet',
			description: 'Get started by creating a new loop',
			actionLabel: 'Create loop',
			onAction: () => navigate('/loops/create/general'),
		}
	}, [searchQuery, navigate])

	// Show spinner while checking permissions or loading data
	if (isCheckingPermission || isLoading) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	// Don't render if not authorized (redirect will happen via useEffect)
	if (!isAuthorized) {
		return null
	}

	const formatStatus = (status: string) => {
		return status
			.split('-')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ')
	}

	const columns: Column<LoopListItem>[] = [
		{
			field: 'name',
			label: 'Name',
			colSpan: 2,
			sortable: true,
			render: (loop: LoopListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{loop.name}
				</Text>
			),
		},
		{
			field: 'status',
			label: 'Status',
			colSpan: 1,
			sortable: true,
			render: (loop: LoopListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{formatStatus(loop.status)}
				</Text>
			),
		},
		{
			field: 'scheduleStartDate',
			label: 'Start Date',
			colSpan: 1,
			sortable: true,
			render: (loop: LoopListItem) => {
				// Handle cases where scheduleStartDate might be empty object or invalid
				const isValidDate =
					loop.scheduleStartDate &&
					typeof loop.scheduleStartDate !== 'object' &&
					loop.scheduleStartDate.toString().trim() !== ''

				return (
					<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
						{isValidDate ? new Date(loop.scheduleStartDate as string | number | Date).toLocaleDateString() : '-'}
					</Text>
				)
			},
		},
		{
			field: 'executionCount',
			label: 'Iterations',
			colSpan: 1,
			sortable: false,
			render: (loop: LoopListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{loop.executionCount || 0}
				</Text>
			),
		},
		{
			field: 'id' as keyof LoopListItem,
			label: 'Actions',
			colSpan: 1,
			sortable: false,
			render: (loop: LoopListItem) => (
				// biome-ignore lint/a11y/noStaticElementInteractions: presentation container for event bubbling
				<div
					role='presentation'
					className='flex justify-start gap-2'
					onClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}>
					{(loop.executionCount ?? 0) > 0 && (
						<Button
							variant='secondary'
							size='small'
							onClick={(e) => {
								e.stopPropagation()
								handleViewResults(loop.id)
							}}
							title='View results'>
							<PieChartIcon className='h-4 w-4' />
						</Button>
					)}
					{!isViewerAdminOnly && loop.actions.includes('duplicateLoop') && (
						<Button
							variant='secondary'
							size='small'
							onClick={(e) => {
								e.stopPropagation()
								handleOpenDuplicateModal(loop)
							}}
							title='Duplicate loop'>
							<CopyIcon className='h-4 w-4' />
						</Button>
					)}
					{!isViewerAdminOnly && loop.actions.includes('deleteLoop') && (
						<Button
							variant='secondary'
							size='small'
							onClick={(e) => {
								e.stopPropagation()
								handleOpenDeleteModal(loop)
							}}
							title='Delete loop'>
							<DeleteIcon className='h-4 w-4' />
						</Button>
					)}
				</div>
			),
		},
	]

	return (
		<main className='w-full p-6 flex flex-col gap-4'>
			<ModuleHeader
				breadcrumb={[{ label: 'Home', to: '/' }, { label: 'Loops' }]}
				title='Loop Overview'
				logo={{
					url: organization?.logo || '',
					name: organization?.name || '',
				}}
			/>

			<GenericTable<LoopListItem, SearchParams & { organizationId: string }>
				data={loops}
				columns={columns}
				idField='id'
				onRowClick={handleRowClick}
				queryOptionsGenerator={queryOptionsGenerator}
				showPagination={false}
				externalDataFetching={true}
				searchValue={searchQuery}
				onSearchChange={handleSearchChange}
				actionButtons={
					!isViewerAdminOnly && actions.includes('createLoop')
						? [
								{
									children: 'Create loop',
									icon: AddIcon,
									iconPosition: 'left',
									onClick: () => navigate('/loops/create/general'),
									variant: 'primary' as const,
								},
							]
						: []
				}
				emptyState={getEmptyState}
			/>

			<Modal
				isOpen={isDuplicateModalOpen}
				onClose={() => {
					setIsDuplicateModalOpen(false)
					setSelectedLoop(null)
				}}
				title='Duplicate Loop'
				buttons={[
					{
						variant: 'secondary',
						onClick: () => {
							setIsDuplicateModalOpen(false)
							setSelectedLoop(null)
						},
						children: 'Cancel',
					},
					{
						variant: 'primary',
						onClick: handleDuplicate,
						children: 'Duplicate',
					},
				]}>
				<div className='space-y-4'>
					<Text variant='small'>
						This will create a copy of the loop <strong>"{selectedLoop?.name}"</strong> with all its configuration.
					</Text>

					<div className='bg-blue-50 p-3 rounded-md'>
						<Text variant='xsmall' className='text-blue-800'>
							<strong>Note:</strong> The duplicated loop will be created as a draft. You'll need to:
						</Text>
						<ul className='mt-2 ml-4 space-y-1 list-disc list-inside'>
							<li>
								<Text variant='xsmall' className='text-blue-700 inline'>
									Review and adjust loop settings if needed
								</Text>
							</li>
							<li>
								<Text variant='xsmall' className='text-blue-700 inline'>
									Update the schedule and launch date
								</Text>
							</li>
							<li>
								<Text variant='xsmall' className='text-blue-700 inline'>
									Launch the loop when ready
								</Text>
							</li>
						</ul>
					</div>
				</div>
			</Modal>

			<Modal
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false)
					setSelectedLoop(null)
				}}
				title='Delete Loop'
				buttons={[
					{
						variant: 'secondary',
						onClick: () => {
							setIsDeleteModalOpen(false)
							setSelectedLoop(null)
						},
						children: 'Cancel',
					},
					{
						variant: 'destructive',
						onClick: handleDelete,
						children: 'Delete',
					},
				]}>
				<div className='space-y-4'>
					<Text variant='small'>
						Are you sure you want to delete the loop <strong>"{selectedLoop?.name}"</strong>?
					</Text>

					<div className='bg-red-50 p-3 rounded-md'>
						<Text variant='xsmall' className='text-red-800'>
							<strong>Warning:</strong> This action cannot be undone. Deleting this loop will:
						</Text>
						<ul className='mt-2 ml-4 space-y-1 list-disc list-inside'>
							<li>
								<Text variant='xsmall' className='text-red-700 inline'>
									Permanently delete all loop data and configurations
								</Text>
							</li>
							<li>
								<Text variant='xsmall' className='text-red-700 inline'>
									Remove all executions and iterations
								</Text>
							</li>
							<li>
								<Text variant='xsmall' className='text-red-700 inline'>
									Delete all participant responses
								</Text>
							</li>
						</ul>
					</div>
				</div>
			</Modal>
		</main>
	)
}
